import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import * as i18next from 'i18next';
import * as api from 'core/api';
import moment from 'moment';
import * as utils from 'core/utils';
import router from 'src/router';
import * as flights from 'core/flights';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const WithRender = require('./template.html');

@WithRender
@Component
export class FlightPassengerEditComponent extends Vue {
    @Prop()
    flightBookingDetails: api.FlightBookingDetails;

    @Prop()
    reservation: flights.ReservationModel;

    @Prop()
    countries: api.Destination[];

    @Prop()
    gaParams: utils.GoogleAnalyticsParams;

    @Prop()
    gaAlreadySubmitted: string[];

    @Prop()
    passenger: flights.PassengerModel;
    
    @Prop()
    searchParams: flights.FlightSearchParams;

    @Prop()
    v: any;

    @Watch('passenger.gender')
    onPassengerGenderChanged(val: string) {
        if (val) {
            if (val.toLowerCase() === 'male') {
                this.passengerGender = flights.Gender.Male;
            }
            else if (val.toLowerCase() === 'female') {
                this.passengerGender = flights.Gender.Female;
            }
        }
    }

    @Watch('passenger.citizenship')
    onPassengerCitizenshipChanged(val: string) {
        if (val) {
            this.passengerCitizenship = val.toString();
        }
    }

    passengerGender: flights.Gender | null = null;

    passengerCitizenship: string | null = null;

    mounted() {
        this.passengerGender = this.passenger.gender ? flights.Gender[this.passenger.gender] : null;
        this.passengerCitizenship = this.passenger.citizenship.toString();

        
        if (this.flightBookingDetails.countryCrossing) {
            this.passenger.checkDocType(this.flightBookingDetails.countryCrossing, this.passenger.citizenship, this.passenger.type);
        }
    }

    get genderOptions() {
        return [
            {
                label: i18next.t('label_gender_' + flights.Gender[flights.Gender.Male].toLowerCase()),
                value: flights.Gender.Male
            },
            {
                label: i18next.t('label_gender_' + flights.Gender[flights.Gender.Female].toLowerCase()),
                value: flights.Gender.Female
            }
        ];
    }

    get selectCountries() {
        return this.countries.map((country) => {
            return {
                label: country.localizedName,
                value: country.uniqueId
            };
        });
    }

    get passengerNamePlaceholder() {
        if (!this.passenger.allowCyrillicName) {
            return i18next.t('label_name_placeholder_latin_only');
        }
        return null;
    }

    get passengerCardAirlineOptions() {
        if (this.flightBookingDetails
            && this.flightBookingDetails.partnerAirlines
            && this.flightBookingDetails.partnerAirlines.length) {
            return this.flightBookingDetails.partnerAirlines
                .map(airline => {
                    return {
                        label: airline.name,
                        value: airline.uniqueId
                    };
                });
        } else {
            return [];
        }
    }

    get hasPassengerCardAirlineOptions() {
        const hasOptions = this.passengerCardAirlineOptions && this.passengerCardAirlineOptions.length;
        if (!this.gaAlreadySubmitted.includes('flights.booking.step1.card.miles.display') && hasOptions) {
            (<any>this).$ga.sendEvent('flights.booking',
                `step1.card.miles.display`,
                `flights.booking.step1.card.miles.display`,
                this.gaParams
            );
            this.gaAlreadySubmitted.push('flights.booking.step1.card.miles.display');
        }
        return hasOptions;
    }

    get selectedAirlineName() {
        if (this.passenger.cardAirlineId) {
            const found = this.flightBookingDetails.partnerAirlines
                .find(airline => airline.uniqueId == this.passenger.cardAirlineId);

            if (found && found.name) {
                return found.name;
            }
        }

        return '';
    }

    get passengerCardFloatingLabel() {
        return i18next.t('label_frequent_flyer_card') +
            (this.selectedAirlineName ? ' ' + this.selectedAirlineName : '');
    }

    onPassengerCitizenshipChange() {
        (<any>this).$emit('change');
        this.passenger.citizenship = this.passengerCitizenship ? parseInt(this.passengerCitizenship) : 0;

        if (!this.passenger.documentNumber) {
            document.getElementById('documentNumber' + this.passenger.index)!.focus();
        }

        this.passenger.checkDocType(this.flightBookingDetails.countryCrossing, this.passenger.citizenship, this.passenger.type);

        if (!this.v.citizenship.isCitizenshipAccept) {
            (<any>this).$ga.sendEvent('flights.booking', 'msg.not.nationality', 'flights.booking.msg.not.nationality', this.gaParams);
            (<any>this.$refs).modalCitizenshipError.show();
        }
    }

    onPassengerGenderChange() {
        (<any>this).$emit('change');
        this.passenger.gender = flights.Gender[this.passengerGender!];

        if (!this.passenger.birthday) {
            this.$nextTick(() => {
                document.getElementById('birthday' + this.passenger.index)!.focus();
            });
        }
    }

    onPassengerDocumentNumberChanged() {
        (<any>this).$emit('change');

        this.passenger.checkDocType(this.flightBookingDetails.countryCrossing, this.passenger.citizenship, this.passenger.type);
    }

    onPassengerDocumentValidTillChanged() {
        if (!this.passenger.documentValidTillEnabled) {
            this.passenger.documentValidTill = '';
        }
    }

    onPassengerDocumentValidTillFocus() {
        if (!this.passenger.documentValidTillRequired) {
            this.passenger.documentValidTillEnabled = true;
        }
    }

    onLastNameBlur() {
        this.v.lastName.$touch();
        this.$emit('passenger-form-blur');
    }

    onFirstNameBlur() {
        this.v.firstName.$touch();
        this.$emit('passenger-form-blur');
    }

    onMiddleNameBlur() {
        this.v.middleName.$touch();
        this.$emit('passenger-form-blur');
    }

    onGenderBlur() {
        this.$emit('passenger-form-blur');
    }

    onBirthdayBlur() {
        this.v.birthday.$touch();
        this.$emit('passenger-form-blur');
    }

    onCitizenshipBlur() {
        this.$emit('passenger-form-blur');
    }

    goToSearch() {
        (<any>this).$ga.sendEvent('flights.booking', 'msg.not.nationality', 'flights.booking.msg.not.nationality.search', this.gaParams);
        router.push(new flights.FlightNavigationService().getSearchResultsUrl(this.searchParams));
    }

    onCloseCitizenshipError() {
        (<any>this.$refs).modalCitizenshipError.hide();
        (<any>this).$ga.sendEvent('flights.booking', 'msg.not.nationality', 'flights.booking.msg.not.nationality.x', this.gaParams);
    }

    onDocumentNumberBlur() {
        this.v.documentNumber.$touch();
        this.$emit('passenger-form-blur');
    }

    onDocumentValidTillBlur() {
        this.v.documentValidTill.$touch();
        this.$emit('passenger-form-blur');
    }

    onDocumentValidTillEnabledBlur() {
        this.$emit('passenger-form-blur');
    }

    onPassengerCardBlur() {
        this.$emit('passenger-form-blur');
    }

    onPassengerCardClick() {
        (<any>this.$refs).modalPassengerCard.show();
    }
}
